import {
  CaseFacility,
  CaseStatus,
  DonorType,
  OrganApproval,
  OrganType,
  ServiceType,
  TrialAnswer,
  TrialQuestion,
} from '@/api/apis/case';
import type { RandomizationResultType } from '@/api/apis/case';
import { hasActiveTrialProgram, isHeartTypeA, getTrialFlowState } from '@/utils/helpers';

export type QuestionnaireItem = {
  question: TrialQuestion;
  answer: TrialAnswer;
};

export type Questionnaire = QuestionnaireItem[] | undefined;

export type TrialProgramLike =
  | {
      isActive?: boolean | null;
      name?: 'ENHANCE' | 'DENOVO';
    }
  | null
  | undefined;

type Params = {
  organType: OrganType;
  donorType: DonorType;
  nextStatus?: CaseStatus;
  serviceType?: ServiceType;
  canMoveToNextStatus: boolean;
  txCenter?: CaseFacility;
  caseTrialProgram?: TrialProgramLike;
  questionnaire?: Questionnaire;
  randomization?: { result?: RandomizationResultType | null } | null;
  organApproval?: OrganApproval | null;
  isReAllocationCase: boolean;
  opoRequest?: boolean | null;
  isRandomizationRequired?: boolean | null;
};

export type TrialProceedBlockReason = 'missingQuestionnaire' | 'missingRandomization' | 'missingOrganApproval' | null;

export const useTrialRandomizationStatus = ({
  organType,
  donorType,
  nextStatus,
  serviceType,
  canMoveToNextStatus,
  txCenter,
  caseTrialProgram,
  questionnaire,
  randomization,
  organApproval,
  opoRequest,
  isRandomizationRequired,
}: Params) => {
  const hasTrialProgram = hasActiveTrialProgram(organType, txCenter?.trialPrograms);
  const isEnhancePartA = isHeartTypeA(questionnaire, organType);

  const hasQuestionnaire = Boolean(questionnaire?.length);
  const randomizationRecorded = Boolean(randomization?.result);
  const organApprovalMissing = organApproval == null;

  const needsQuestionnaire = hasTrialProgram && !hasQuestionnaire;
  const needsRandomization = Boolean(isRandomizationRequired) && !randomizationRecorded;

  const isDirectAcquisitionCase = serviceType === ServiceType.DirectAcquisition;
  const isProceedToOnOcs = nextStatus === CaseStatus.OrganOnOCS;
  const isProceedToPendingLogistics = nextStatus === CaseStatus.PendingLogistics;
  const isOpoCase = opoRequest === true;

  const { shouldBlockFirstTimeTrialQuestionnaire } = getTrialFlowState({
    organType,
    donorType,
    txCenter,
    caseTrialProgram,
    organApproval,
    isEnhancePartA,
  });

  const shouldEvaluateTrialBlock = hasTrialProgram && !isDirectAcquisitionCase && canMoveToNextStatus;

  const shouldBlockPendingLogistics =
    shouldEvaluateTrialBlock && !isOpoCase && isProceedToPendingLogistics && needsQuestionnaire;

  const shouldBlockOnOcs =
    shouldEvaluateTrialBlock &&
    isOpoCase &&
    isProceedToOnOcs &&
    (shouldBlockFirstTimeTrialQuestionnaire || needsQuestionnaire || needsRandomization);

  let proceedBlockReason: TrialProceedBlockReason = null;

  if (shouldBlockPendingLogistics) {
    proceedBlockReason = 'missingQuestionnaire';
  }

  if (shouldBlockOnOcs) {
    if (needsQuestionnaire) {
      proceedBlockReason = 'missingQuestionnaire';
    } else if (needsRandomization) {
      proceedBlockReason = 'missingRandomization';
    } else if (organApprovalMissing && shouldBlockFirstTimeTrialQuestionnaire) {
      proceedBlockReason = 'missingOrganApproval';
    }
  }

  return {
    proceedPendingLogisticsDisabled: shouldBlockPendingLogistics,
    proceedOnOCSDisabled: shouldBlockOnOcs,
    proceedBlockReason,
    isDirectAcquisitionCase,
  };
};
